/**
 * Phase 3.5.3 — Display-only scoreboard buffer (anti-spoiler).
 *
 * The server applies a SCORING action and broadcasts the new state BEFORE
 * the reveal events arrive at the client. Without a buffer, the scoreboard
 * jumps from 0-0 to 1-0 a frame BEFORE the ShotSequence / JokerSequence
 * starts animating — the player can read the outcome on the scoreboard
 * before the cards are revealed.
 *
 * This hook returns a deferred score that lags behind the real state until
 * the reveal animations finish:
 *
 *   1. When `realP1` / `realP2` changes from the previously-seen value,
 *      arm an internal hold until `Date.now() + SCORE_HOLD_MS`. Detection
 *      happens synchronously DURING render via a ref comparison, so the
 *      very same render that would otherwise leak the new score now
 *      reports `guarded=true` and emits the previous (safe) score.
 *
 *   2. The caller also passes `holdActive` (true while pendingReveal /
 *      turn-pass / penalty holds are live). The display score stays
 *      buffered while EITHER guard is active.
 *
 *   3. When the guard clears, the safe ref advances to the real score
 *      and the displayed value updates.
 *
 * Backend scoring logic is untouched — this is purely a display lag.
 */
import { useEffect, useRef, useState } from 'react';

const SCORE_HOLD_MS = 1800;

export function useDeferredScores(realP1, realP2, holdActive) {
  const safeRef = useRef({ p1: realP1, p2: realP2 });
  const lastSeenRef = useRef({ p1: realP1, p2: realP2 });
  const [scoreHoldUntil, setScoreHoldUntil] = useState(0);

  // Synchronous change detection — true on the FIRST render where the
  // real score advanced past the last-seen value. This is critical: it
  // makes `guarded` true on that very render so the new score never
  // reaches the JSX.
  const changedThisRender =
    realP1 !== lastSeenRef.current.p1 || realP2 !== lastSeenRef.current.p2;

  const guarded =
    holdActive || Date.now() < scoreHoldUntil || changedThisRender;

  // Side effect: arm the hold + remember the new "last seen" so subsequent
  // renders don't keep reporting changed=true.
  useEffect(() => {
    if (changedThisRender) {
      setScoreHoldUntil(Date.now() + SCORE_HOLD_MS);
      lastSeenRef.current = { p1: realP1, p2: realP2 };
    }
  }, [changedThisRender, realP1, realP2]);

  // Once the guard clears, the displayed score safely catches up.
  useEffect(() => {
    if (!guarded) {
      safeRef.current = { p1: realP1, p2: realP2 };
    }
  }, [guarded, realP1, realP2]);

  return guarded
    ? { p1: safeRef.current.p1, p2: safeRef.current.p2 }
    : { p1: realP1, p2: realP2 };
}
